import React, { useState, useEffect } from 'react';
import { toAbsoluteUrl } from './AssetHelpers';
import { useAuth } from '../../app/modules/auth';

interface AccordionItemProps {
    id: number;
    title: string;
    country: string;
    processedBetween: string;
    imageSrc: string;
    organization: string;
    description: string;
    certifications: string;
    expandedAccordion: number | null;
    onToggle: (id: number) => void;
}

interface DdpCProps {
    user: any;
}

interface ProgressBarProps {
    label: string;
    percentage: number;
    colorClass: string;
}

interface ApiDataType {
    id?: string;
    categoryId?: string;
    categoryName?: string;
    itemName?: string;
    itemId?: string;
    franchiseId?: string;
    bomDetails?: any[];
    productName?: string;
    description?: string;
    dimension?: string;
    weight?: string;
    productImages?: Array<{
        url: string;
        image: string;
        text?: string | null;
    }>;
    bom?: string;
    aboutBrand?: string;
    aboutItem?: string;
    packaging?: string;
    processes?: Array<{
        processId: string;
        processName: string;
        facilityName: string;
        address: string;
        country: string;
        image: string;
        action: boolean;
        certifications: string;
    }>;
    socialImpact?: Array<{
        url: string;
        image: string;
    }>;
    recycleInfo?: Array<{
        url: string;
        image: string;
        text: string;
    }>;
    reuseInfo?: Array<{
        url: string;
        image: string;
        text: string;
    }>;
    repairInfo?: Array<{
        url: string;
        image: string;
        text: string;
    }>;
    co2Savings?: number;
    energySavings?: number;
    waterSavings?: number;
    treeSavings?: number;
}

const DdpC2: React.FC<DdpCProps> = ({ user }) => {
    console.log("user this", user);

    const [apiData, setApiData] = useState<ApiDataType | null>(null);
    const [expandedSection, setExpandedSection] = useState<string>('details');
    const [expandedAccordion, setExpandedAccordion] = useState<number | null>(null);
    const [activeTab, setActiveTab] = useState<string>('description');
    const [mainImage, setMainImage] = useState('');
    const [mainImage2, setMainImage2] = useState('/sibig.jpg');
    const [progressValues, setProgressValues] = useState({
        co2: 70,
        water: 50,
        waste: 30
    });

    useEffect(() => {
        const fetchDppData = async () => {
            try {
                const authData = localStorage.getItem('kt-auth-react-v');
                const token = authData ? JSON.parse(authData).data.token : null;

                const response = await fetch(`https://inclusive-backend-api.asmglobal.co.in/v1/api/dpp/${user.assignedDppId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    console.log('DPP data:', data);
                    setApiData(data.data);
                } else {
                    console.error('Failed to fetch DPP data');
                }
            } catch (error) {
                console.error('Error fetching DPP data:', error);
            }
        };

        if (user?.assignedDppId) {
            fetchDppData();
        }
    }, [user?.assignedDppId]);

    useEffect(() => {
        if (apiData?.productImages?.[0]?.url) {
            setMainImage(apiData.productImages[0].url);
        }
        if (apiData?.socialImpact?.[0]?.url) {
            setMainImage2(apiData.socialImpact[0].url);
        }
    }, [apiData]);

    useEffect(() => {
        // Animate progress bars on component mount
        const timer = setTimeout(() => {
            setProgressValues({ co2: 70, water: 50, waste: 30 });
        }, 500);
        return () => clearTimeout(timer);
    }, []);

    const toggleSection = (section: string) => {
        const newSection = expandedSection === section ? '' : section;
        setExpandedSection(newSection);

        // Auto-open first accordion when Material Traceability is opened
        if (section === 'traceability' && newSection === 'traceability') {
            setExpandedAccordion(1);
        } else if (newSection === '') {
            setExpandedAccordion(null);
        }
    };

    const toggleAccordion = (id: number) => {
        setExpandedAccordion(expandedAccordion === id ? null : id);
    };

    const toggleContent = (contentId: string) => {
        setActiveTab(contentId);
    };

    const changeImage = (src: string) => {
        setMainImage(src);
    };

    const changeImage2 = (src: string) => {
        setMainImage2(src);
    };

    const AccordionItem: React.FC<AccordionItemProps> = ({
        id,
        title,
        country,
        processedBetween,
        imageSrc,
        organization,
        description,
        certifications,
        expandedAccordion,
        onToggle
    }) => {
        const isExpanded = expandedAccordion === id;

        return (
            <div className="flex gap-3 mt-4 mb-3">
                <div className="bg-white rounded-lg shadow-lg flex-1">
                    <div className="p-4 cursor-pointer" onClick={() => onToggle(id)}>
                        <div className="flex justify-between items-center mb-4">
                            <div className="text-base font-bold mb-1">{title}</div>
                            <svg
                                className={`w-5 h-5 text-gray-500 transition-transform transform ${isExpanded ? 'rotate-180' : ''}`}
                                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                            </svg>
                        </div>
                        <div>
                            <div className="flex justify-between items-center">
                                <p className="font-medium text-sm">Country</p>
                                <p className=" text-sm">{country}</p>
                            </div>
                            <div className="flex justify-between items-center">
                                <p className="font-medium text-sm">Facility:</p>
                                <p className="text-sm">{processedBetween}</p>
                            </div>
                        </div>

                        <div className={`transition-all duration-300 overflow-hidden ${isExpanded ? 'max-h-screen' : 'max-h-0'}`}>
                            <hr className="w-full my-3" />
                            <img className="rounded-lg mb-4 bg-gray-200 w-full object-cover" style={{ height: 'auto' }} src={imageSrc} alt={title} />
                            <p className="text-base font-bold mb-1">Organization</p>
                            <div className="text-sm mb-4 ">{organization}</div>
                            <div className="text-sm ">{description}</div>
                            <div className="flex justify-between items-center pt-2">
                                <p className="text-base font-bold mb-1">Certifications:</p>
                                <p className=" text-sm">{certifications}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const ProgressBar: React.FC<ProgressBarProps> = ({ label, percentage, colorClass }) => (
        <div className="my-5">
            <label className="block mb-1 text-left text-sm font-medium">{label}</label>
            <div className="flex items-center justify-between">
                <div className="bg-gray-200 rounded-full overflow-hidden h-4 w-4/5">
                    <div
                        className={`h-full rounded-full transition-all duration-1000 ease-out ${colorClass}`}
                        style={{ width: `${percentage}%` }}
                    ></div>
                </div>
                <span className="block mt-0 font-bold text-sm">{percentage}%</span>
            </div>
        </div>
    );

    const traceabilityData = apiData?.processes?.map((process, index) => ({
        id: index + 1,
        title: process.processName,
        country: process.country,
        processedBetween: process.facilityName,
        imageSrc: process.image,
        organization: process.facilityName,
        description: `${process.facilityName} located at ${process.address}`,
        certifications: process.certifications,
    })) || [];

    const { currentUser, auth, isFacilityManager, isSupplyChainManager }: any = useAuth()

    const image = isSupplyChainManager
        ? auth?.data?.profileImage || ''
        : isFacilityManager
            ? toAbsoluteUrl('/media/avatars/facMan.jpeg')
            : isSupplyChainManager
                ? currentUser?.franchiseKycDoc?.filter((x) => x?.docType === 'SEC_DTI')?.[0]?.docUrl
                : currentUser?.kycDocument?.filter((x) => x?.docType === 'SEC_DTI')?.[0]?.docUrl ||
                currentUser?.kycDocument?.filter((x) => x?.docType === 'CLIENT_LOGO')?.[0]?.docUrl

    if (!apiData) {
        return <div>Loading...</div>;
    }

    return (
        <div className="bg-gray-50 min-h-screen" style={{ color: '#333', fontFamily: 'Inter, Arial, sans-serif', marginTop: "-40px" }}>
            <div style={{ backgroundColor: 'white', height: '80px', width: '100%' }}></div>
            <div className="px-4  mx-auto" style={{ position: 'relative', zIndex: 10, marginTop: '-40px' }}>
                <div className="relative">
                    {/* Product Details Section */}
                    <div className="bg-white shadow-lg rounded-lg relative z-10 mb-2 mt-5">
                        <div className="p-1 cursor-pointer" onClick={() => toggleSection('details')}>
                            <div className="flex justify-between items-center mb-4">
                                <img className="h-12 rounded" src={image || toAbsoluteUrl('/media/avatars/profile.jpeg')} alt='' />
                                <svg
                                    className={`w-5 h-5 text-gray-500 transition-transform transform ${expandedSection === 'details' ? 'rotate-180' : ''}`}
                                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                </svg>
                            </div>
                        </div>

                        <div className={`transition-all duration-300 overflow-hidden ${expandedSection === 'details' ? 'block' : 'hidden'}`}>
                            <div className="p-2">
                                <p className="mb-2 text-sm">
                                    {apiData?.description}
                                </p>
                                <hr className="border-t-2 border-gray-300" />
                                <h1 className="text-md font-bold mb-2 pt-2 text-sm">
                                    {apiData?.productName}
                                </h1>
                                <hr className="border-t-2 border-gray-300 p-2 mt-2" />

                                <div className="text-center">
                                    <div className="flex justify-center">
                                        <img
                                            src={mainImage}
                                            alt="Main Image"
                                            className="w-4/5 h-auto rounded-lg"
                                        />
                                    </div>

                                    <div className="flex justify-center mt-5">
                                        {apiData?.productImages?.slice(1)?.map((img, index) => (
                                            <img
                                                key={index}
                                                src={img.url}
                                                alt={`Thumbnail ${index + 1}`}
                                                className="w-24 h-auto mx-2 rounded-lg cursor-pointer hover:scale-105 transition-transform"
                                                onClick={() => changeImage(img.url)}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div className="flex justify-between items-center mt-4">
                                    <p style={{ background: '#12B886' }} className="text-white w-24 text-center py-1 mb-1 rounded text-xs">Best Seller</p>
                                    <p className="text-sm"></p>
                                </div>

                                <div className="my-1">
                                    <div className="flex justify-between items-center px-0">
                                        <p className={`text-sm cursor-pointer mx-2 ${activeTab === 'description' ? 'border-b-2 border-green-500' : ''}`} onClick={() => toggleContent('description')}>Description</p>
                                        <p className={`text-sm cursor-pointer mx-2 ${activeTab === 'practical-info' ? 'border-b-2 border-green-500' : ''}`} onClick={() => toggleContent('practical-info')}>Practical Information</p>
                                        <p className={`text-sm cursor-pointer mx-2 ${activeTab === 'our-mission' ? 'border-b-2 border-green-500' : ''}`} onClick={() => toggleContent('our-mission')}>
                                            Specification
                                        </p>
                                    </div>

                                    {activeTab === 'description' && (
                                        <div className="p-2 border border-blue-200 rounded mt-2">
                                            <p className="text-sm">{apiData?.aboutBrand}</p>
                                        </div>
                                    )}

                                    {activeTab === 'practical-info' && (
                                        <div className="p-2 border border-blue-200 rounded mt-2">
                                            <div className="text-sm">
                                                {apiData?.aboutItem}
                                            </div>
                                        </div>
                                    )}

                                    {activeTab === 'our-mission' && (
                                        <div className="p-2 border border-blue-200 rounded mt-2">
                                            <p className="text-sm">
                                                <div className="space-y-3">
                                                    <div className="flex justify-between items-center">
                                                        <span className="font-semibold text-sm">Material</span>
                                                        <span className="text-sm">{apiData?.categoryName}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="font-semibold text-sm">Plastics</span>
                                                        <span className="text-sm ">{apiData?.itemName}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="font-semibold text-sm">Product</span>
                                                        <span className="text-sm ">{apiData?.productName}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="font-semibold text-sm">Dimension</span>
                                                        <span className="text-sm ">{apiData?.dimension}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="font-semibold text-sm">Weight</span>
                                                        <span className="text-sm ">{apiData?.weight}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="font-semibold text-sm">Packaging</span>
                                                        <span className="text-sm ">{apiData?.packaging || '-'}</span>
                                                    </div>
                                                </div>
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>



                    <div className="bg-white rounded-lg shadow-lg relative z-10 mb-2 mt-5">
                        <div className="p-2 cursor-pointer" onClick={() => toggleSection('manufacturing')}>
                            <div className="flex justify-between items-center mb-4">
                                <div className="text-base mb-1" style={{ fontSize: '20px' }}>Manufacturing Information</div>
                                <svg
                                    className={`w-5 h-5 text-gray-500 transition-transform transform ${expandedSection === 'manufacturing' ? 'rotate-180' : ''}`}
                                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                </svg>
                            </div>
                        </div>
                        <div className={`transition-all duration-300 overflow-hidden ${expandedSection === 'manufacturing' ? 'block' : 'hidden'}`}>
                            <div className="p-4">
                                <div className="flex justify-between items-start mb-3">
                                    <span className="font-bold text-sm flex-shrink-0 mr-3">Batch id</span>
                                    <span className="text-sm text-right">{user?.productionId}</span>
                                </div>
                                <div className="flex justify-between items-start mb-3">
                                    <span className="font-bold text-sm flex-shrink-0 mr-3">Batch Weight</span>
                                    <span className="text-sm text-right">
                                        {parseFloat(user?.productionItemDetails?.[0]?.quantity || 0).toFixed(2)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-start mb-3">
                                    <span className="font-bold text-sm flex-shrink-0 mr-3">Date of Production</span>
                                    <span className="text-sm text-right">
                                        {user?.actualProcessDate ? new Date(user.actualProcessDate).toLocaleDateString() : 'N/A'}
                                    </span>
                                </div>
                                <div className="flex justify-between items-start mb-3">
                                    <span className="font-bold text-sm flex-shrink-0 mr-3">Facility Name</span>
                                    <span className="text-sm text-right">{user?.pickupPointName}</span>
                                </div>
                                <div className="flex flex-col mb-3">
                                    <span className="font-bold text-sm mb-1">Address</span>
                                    <span className="text-sm">
                                        {user?.pickupPointInfo?.address?.street}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>



                    {/* Material Traceability Section */}
                    <div className="bg-white rounded-lg shadow-lg relative z-10 mb-2 mt-5">
                        <div className="p-2 cursor-pointer" onClick={() => toggleSection('traceability')}>
                            <div className="flex justify-between items-center mb-4">
                                <div className="text-base mb-1" style={{ fontSize: '20px' }}>Material Traceability</div>
                                <svg
                                    className={`w-5 h-5 text-gray-500 transition-transform transform ${expandedSection === 'traceability' ? 'rotate-180' : ''}`}
                                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                </svg>
                            </div>
                        </div>

                        <div className={`transition-all duration-300 overflow-hidden ${expandedSection === 'traceability' ? 'block' : 'hidden'}`}>
                            <div className="p-4">
                                {traceabilityData.map((item) => (
                                    <AccordionItem
                                        key={item.id}
                                        {...item}
                                        expandedAccordion={expandedAccordion}
                                        onToggle={toggleAccordion}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Impact Section */}
                    <div className="bg-white rounded-lg shadow-lg relative z-10 mb-2 mt-5 text-center">
                        <div className="p-2 cursor-pointer" onClick={() => toggleSection('impact')}>
                            <div className="flex justify-between items-center mb-4">
                                <div className="text-base mb-1 text-center" style={{ fontSize: '20px' }}>Impact</div>
                                <svg
                                    className={`w-5 h-5 text-gray-500 transition-transform transform ${expandedSection === 'impact' ? 'rotate-180' : ''}`}
                                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                </svg>
                            </div>
                        </div>

                        <div className={`transition-all duration-300 overflow-hidden ${expandedSection === 'impact' ? 'block' : 'hidden'}`}>
                            <div className="grid grid-cols-2 gap-3 mb-4">
                                <div className="flex items-center p-3 bg-white rounded-lg shadow-sm">
                                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                                        <img src="/media/svg/dashboard/GHG_saving.png" alt="GHG" className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-gray-700">GHG Savings</p>
                                        <p className="text-sm font-bold">{apiData?.co2Savings || 0}%</p>
                                    </div>
                                </div>

                                <div className="flex items-center p-3 bg-white rounded-lg shadow-sm">
                                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                        <img src="/media/svg/dashboard/energy_saved.png" alt="Energy" className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-gray-700">Energy Saved</p>
                                        <p className="text-sm font-bold">{apiData?.energySavings || 0}%</p>
                                    </div>
                                </div>

                                <div className="flex items-center p-3 bg-white rounded-lg shadow-sm">
                                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                                        <img src="/media/svg/dashboard/waste_saved.png" alt="Waste" className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-gray-700">Waste Saved</p>
                                        <p className="text-sm font-bold">{apiData?.waterSavings || 0}%</p>
                                    </div>
                                </div>

                                <div className="flex items-center p-3 bg-white rounded-lg shadow-sm">
                                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                                        <img src="/media/svg/dashboard/trees_saved.png" alt="Trees" className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-gray-700">Trees Saved</p>
                                        <p className="text-sm font-bold">{apiData?.treeSavings || 0}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="p-1 bg-white rounded-lg shadow-lg relative z-10 mb-1 mt-1">
                                <hr className="border-t-2 border-gray-300 mb-2" />
                                <div className="text-base font-bold mb-1">Social Impact</div>
                                <hr className="border-t-2 border-gray-300 p-2 mt-2" />

                                <div className="text-center">
                                    <div className="flex justify-center mt-5 mb-5">
                                        {apiData?.socialImpact?.map((impact, index) => (
                                            <img
                                                key={index}
                                                src={impact.url}
                                                alt={`Social Impact ${index + 1}`}
                                                className="w-24 h-auto mx-2 rounded-lg cursor-pointer hover:scale-105 transition-transform"
                                                onClick={() => changeImage2(impact.url)}
                                            />
                                        ))}
                                    </div>
                                    <div className="pb-2 px-4">
                                        <img src={mainImage2} alt="Main Image" className="w-full h-auto rounded-lg" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* End of Life Section */}
                    <div className="bg-white rounded-lg shadow-lg relative z-10 mb-2 mt-5">
                        <div className="p-2 cursor-pointer" onClick={() => toggleSection('End of Life')}>
                            <div className="flex justify-between items-center mb-4">
                                <div className="text-base mb-1" style={{ fontSize: '20px' }}>End of Life</div>
                                <svg
                                    className={`w-5 h-5 text-gray-500 transition-transform transform ${expandedSection === 'End of Life' ? 'rotate-180' : ''}`}
                                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                </svg>
                            </div>
                        </div>
                        <div className={`transition-all duration-300 overflow-hidden ${expandedSection === 'End of Life' ? 'block' : 'hidden'}`}>
                            <div className="p-4">
                                {/* Reuse Section */}
                                <p className="text-sm text-black font-semibold mb-3 text-left">Reuse Instructions</p>
                                <div className="text-sm text-black mb-3">
                                    {apiData?.reuseInfo?.[0]?.text}
                                </div>
                                <div className="mb-4">
                                    <img
                                        alt="Reuse Info"
                                        src={apiData?.reuseInfo?.[0]?.image}
                                        className="w-full h-auto rounded-lg"
                                        style={{
                                            filter: 'invert(1)',
                                        }}
                                    />
                                </div>
                                <div className="text-sm text-blue-500 underline mb-6">
                                    <a href={apiData?.reuseInfo?.[0]?.url} target="_blank" rel="noopener noreferrer">
                                        {apiData?.reuseInfo?.[0]?.url}
                                    </a>
                                </div>

                                {/* Repair Section */}
                                <p className="text-sm text-black font-semibold mb-3 text-left">Repair Instructions</p>
                                <div className="text-sm text-black mb-3">
                                    {apiData?.repairInfo?.[0]?.text}
                                </div>
                                <div className="mb-4">
                                    <img
                                        alt="Repair Info"
                                        src={apiData?.repairInfo?.[0]?.image}
                                        className="w-full h-auto rounded-lg"
                                        style={{
                                            filter: 'invert(1)',
                                        }}
                                    />
                                </div>
                                <div className="text-sm text-blue-500 underline mb-6">
                                    <a href={apiData?.repairInfo?.[0]?.url} target="_blank" rel="noopener noreferrer">
                                        {apiData?.repairInfo?.[0]?.url}
                                    </a>
                                </div>

                                {/* Recycle Section */}
                                <p className="text-sm text-black font-semibold mb-3 text-left">Recycle Instructions</p>
                                <div className="text-sm text-black mb-3">
                                    {apiData?.recycleInfo?.[0]?.text}
                                </div>
                                <div className="mb-4">
                                    <img
                                        alt="Recycle Info"
                                        src={apiData?.recycleInfo?.[0]?.image}
                                        className="w-full h-auto rounded-lg"
                                        style={{
                                            filter: 'invert(1)',
                                        }}
                                    />
                                </div>
                                <div className="text-sm text-blue-500 underline">
                                    <a href={apiData?.recycleInfo?.[0]?.url} target="_blank" rel="noopener noreferrer">
                                        {apiData?.recycleInfo?.[0]?.url}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="p-1 bg-white rounded-lg shadow-lg relative z-10 mb-1 mt-5">
                        <div className="p-4 flex justify-center">
                            <img
                                alt="Logo"
                                src="/media/logos/enverify2.png"
                                className="app-sidebar-logo-default"
                                style={{
                                    filter: 'invert(1)',
                                    width: '50%',
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DdpC2;